import { type CSSProperties, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Avatar, Button, Icon, IconButton, Input, Logo, SidebarNav, type NavItem } from '../design';
import { ROLE_LABELS } from '../lib/types';

const ROUTES: Record<string, string> = {
  overview: '/',
  clients: '/clients',
  sessions: '/sessions',
  library: '/library',
  orders: '/orders',
  settings: '/settings',
  'admin-users': '/admin/users',
};

const META: Record<string, { title: string; subtitle: string }> = {
  overview: { title: 'Overview', subtitle: '' },
  clients: { title: 'Clients', subtitle: 'The people you work with' },
  sessions: { title: 'Sessions', subtitle: 'Book, reschedule and join sessions' },
  library: { title: 'Program library', subtitle: 'Hypnosis, affirmation and ultrasonic programs' },
  orders: { title: 'Orders', subtitle: 'Store orders from zenmindware.com' },
  settings: { title: 'Settings', subtitle: 'Profile, delivery defaults and notifications' },
  'admin-users': { title: 'Users', subtitle: 'Manage portal access and roles' },
  'client-detail': { title: 'Client', subtitle: '' },
};

const menuItemStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: 'var(--space-4) var(--space-5)',
  background: 'none',
  border: 0,
  font: 'var(--type-body-sm)',
  color: 'var(--text-strong)',
  cursor: 'pointer',
};

/** Which sidebar item is highlighted. Nested routes keep their parent selected. */
function activeValue(pathname: string): string {
  if (pathname === '/') return 'overview';
  if (pathname.startsWith('/admin/users')) return 'admin-users';
  return pathname.slice(1).split('/')[0] || 'overview';
}

/**
 * Which page header to show. Split from activeValue because a detail route wants
 * its own title while keeping the parent nav item highlighted — otherwise
 * /clients/:id renders "Clients · The people you work with" above one person.
 */
function metaKey(pathname: string): string {
  if (/^\/clients\/[^/]+$/.test(pathname)) return 'client-detail';
  return activeValue(pathname);
}

export function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!user) return null;

  const value = activeValue(location.pathname);
  const meta = META[metaKey(location.pathname)] ?? META.overview;
  const first = user.name.split(' ')[0] || user.name;
  const subtitle = value === 'overview' ? `Welcome back, ${first}.` : meta.subtitle;

  // The Clients screen is practitioner-scoped, so a client-role user would only
  // be bounced back to /. No count badge: it would mean a fetch in the layout on
  // every route change, with no shared cache, for a decorative number.
  // Mirrors the route guards exactly (roles={['practitioner']}, admins superset).
  // Keying off isPractitioner instead would drift: role and the Practitioner row
  // are independent axes. Guards the Clients AND Orders nav items.
  const isManager = user.role === 'admin' || user.role === 'practitioner';

  const items: NavItem[] = [
    { section: 'Practice' },
    { value: 'overview', label: 'Overview', icon: 'layout-dashboard' },
    ...(isManager ? ([{ value: 'clients', label: 'Clients', icon: 'users' }] as NavItem[]) : []),
    { value: 'sessions', label: 'Sessions', icon: 'calendar' },
    { section: 'Catalogue' },
    { value: 'library', label: 'Program library', icon: 'headphones' },
    ...(isManager ? ([{ value: 'orders', label: 'Orders', icon: 'shopping-bag' }] as NavItem[]) : []),
    ...(user.role === 'admin'
      ? ([{ section: 'Admin' }, { value: 'admin-users', label: 'Users', icon: 'user-cog' }] as NavItem[])
      : []),
    { section: 'Account' },
    { value: 'settings', label: 'Settings', icon: 'settings' },
  ];

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '248px 1fr', minHeight: '100vh', background: 'var(--surface-page)' }}>
      <aside
        style={{
          background: 'var(--navy-900)',
          padding: 'var(--space-6) var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-5)',
        }}
      >
        <div style={{ padding: '0 var(--space-3) var(--space-3)' }}>
          <Logo theme="white" height={32} />
        </div>

        <SidebarNav value={value} onChange={(v) => navigate(ROUTES[v] || '/')} items={items} />

        <div style={{ marginTop: 'auto', position: 'relative' }}>
          {menuOpen && (
            <div
              style={{
                position: 'absolute',
                bottom: 'calc(100% + 8px)',
                left: 0,
                right: 0,
                background: 'var(--white)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-lg)',
                overflow: 'hidden',
              }}
            >
              <button
                style={menuItemStyle}
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/settings');
                }}
              >
                Settings
              </button>
              <button style={{ ...menuItemStyle, color: 'var(--clay-600)' }} onClick={onLogout}>
                Sign out
              </button>
            </div>
          )}
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-4)',
              padding: 'var(--space-4)',
              border: 'none',
              borderTop: '1px solid rgba(255,255,255,.1)',
              background: 'none',
              width: '100%',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <Avatar name={user.name} size="sm" />
            <div style={{ minWidth: 0 }}>
              <div style={{ font: 'var(--type-body-sm)', color: 'var(--white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.name}
              </div>
              <div style={{ font: 'var(--type-caption)', color: 'var(--navy-300)' }}>{ROLE_LABELS[user.role]}</div>
            </div>
            <Icon name={menuOpen ? 'chevron-down' : 'chevron-up'} size="14px" style={{ color: 'var(--navy-300)', marginLeft: 'auto' }} />
          </button>
        </div>
      </aside>

      <main style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-6)',
            padding: 'var(--space-6) var(--space-8)',
            background: 'var(--white)',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h1 style={{ font: 'var(--type-h2)' }}>{meta.title}</h1>
            {subtitle && <div style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)', marginTop: 4 }}>{subtitle}</div>}
          </div>
          <div style={{ marginLeft: 'auto', width: 260 }}>
            <Input icon="search" placeholder="Search clients, programs" />
          </div>
          <IconButton icon="bell" label="Notifications" />
          {/* Clients book on /sessions. For practitioners that page is a
              read-only list — their entry is Clients → detail → Book session —
              so the CTA would be a dead end and is hidden. */}
          {user.role === 'client' && (
            <Button size="sm" icon="plus" onClick={() => navigate('/sessions')}>
              Book session
            </Button>
          )}
        </header>
        <div style={{ padding: 'var(--space-8)', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
